import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, X, Minus, Plus, MapPin, Phone, User, Truck, Store, Loader2, Clock } from 'lucide-react';

// â”€â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface StoreInfo {
  id: string;
  name: string;
  logo?: string;
  slug: string;
  deliveryFee: number;
  minOrder: number;
  acceptsDelivery: boolean;
  acceptsPickup: boolean;
  pixKey?: string;
}

interface Product {
  id: string;
  name: string;
  price: number;
  unit: string;
  category: string;
  image?: string;
  inStock: boolean;
}

interface CartItem {
  product: Product;
  quantity: number;
}

interface OrderResponse {
  id: string;
  total: number;
  pixCode?: string;
  pixQrCode?: string;
}

// â”€â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const API_BASE = import.meta.env.VITE_API_URL || '';

// â”€â”€â”€ Helper Components â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

// â”€â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function StorePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // State
  const [storeInfo, setStoreInfo] = React.useState<StoreInfo | null>(null);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState<string>('Todos');
  const [cart, setCart] = React.useState<CartItem[]>([]);
  const [showCart, setShowCart] = React.useState(false);
  const [showCheckout, setShowCheckout] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [orderResult, setOrderResult] = React.useState<OrderResponse | null>(null);

  // Checkout form
  const [customerName, setCustomerName] = React.useState('');
  const [customerPhone, setCustomerPhone] = React.useState('');
  const [customerAddress, setCustomerAddress] = React.useState('');
  const [deliveryType, setDeliveryType] = React.useState<'entrega' | 'retirada'>('entrega');

  // â”€â”€â”€ Data Fetching â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  React.useEffect(() => {
    if (!slug) return;

    async function fetchData() {
      try {
        setLoading(true);
        const [infoRes, productsRes] = await Promise.all([
          fetch(`${API_BASE}/api/store/${slug}/info`),
          fetch(`${API_BASE}/api/store/${slug}/products`),
        ]);

        if (!infoRes.ok) {
          setError('Loja nÃ£o encontrada');
          return;
        }

        const info = await infoRes.json();
        const prods = await productsRes.json();
        setStoreInfo({
          id: slug!,
          name: info.name,
          logo: info.logo,
          slug: slug!,
          deliveryFee: info.delivery?.fee || 5,
          minOrder: info.delivery?.min_order || 20,
          acceptsDelivery: info.delivery?.enabled !== false,
          acceptsPickup: info.delivery?.pickup_enabled !== false,
        });
        setProducts(prods.products || prods || []);
      } catch {
        setError('Erro ao carregar a loja');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [slug]);

  // â”€â”€â”€ Derived State â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const categories = React.useMemo(() => {
    const cats = new Set(products.map((p) => p.category).filter(Boolean));
    return ['Todos', ...Array.from(cats)];
  }, [products]);

  const filteredProducts = React.useMemo(() => {
    return products.filter((p) => {
      const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = selectedCategory === 'Todos' || p.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, search, selectedCategory]);

  const cartTotal = React.useMemo(() => {
    return cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  }, [cart]);

  const cartItemCount = React.useMemo(() => {
    return cart.reduce((sum, item) => sum + (item.product.unit === 'kg' ? 1 : item.quantity), 0);
  }, [cart]);

  const deliveryFee = deliveryType === 'entrega' ? (storeInfo?.deliveryFee || 0) : 0;
  const orderTotal = cartTotal + deliveryFee;

  // â”€â”€â”€ Cart Actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  function addToCart(product: Product, qty?: number) {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        const increment = qty || (product.unit === 'kg' ? 0.1 : 1);
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: Math.round((item.quantity + increment) * 100) / 100 }
            : item
        );
      }
      return [...prev, { product, quantity: qty || (product.unit === 'kg' ? 0.5 : 1) }];
    });
  }

  function removeFromCart(productId: string) {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  }

  function updateCartQuantity(productId: string, quantity: number) {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, quantity: Math.round(quantity * 100) / 100 } : item
      )
    );
  }

  // â”€â”€â”€ Checkout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  async function handleSubmitOrder() {
    if (!slug || !customerName || !customerPhone) return;
    if (deliveryType === 'entrega' && !customerAddress) return;

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/store/${slug}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: customerName,
          customer_phone: customerPhone,
          customer_address: deliveryType === 'entrega' ? customerAddress : 'Retirada no local',
          delivery_type: deliveryType,
          items: cart.map((item) => ({
            product_id: item.product.id,
            quantity: item.quantity,
          })),
          notes: undefined,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        alert(err.error || 'Erro ao criar pedido');
        return;
      }

      const order: OrderResponse = await res.json();
      setOrderResult(order);
    } catch {
      alert('Erro ao enviar pedido. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  // â”€â”€â”€ Loading / Error States â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  if (loading) {
    return (
      <div className="store-page flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error || !storeInfo) {
    return (
      <div className="store-page flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
        <Store className="h-16 w-16 text-gray-300 mb-4" />
        <h1 className="text-xl font-semibold text-gray-700">{error || 'Loja nÃ£o encontrada'}</h1>
        <p className="text-gray-500 mt-2">Verifique o link e tente novamente.</p>
      </div>
    );
  }

  // â”€â”€â”€ PIX / Order Confirmation â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  if (orderResult) {
    return (
      <div className="store-page min-h-screen bg-gray-50 p-4">
        <div className="mx-auto max-w-md pt-8">
          <div className="rounded-2xl bg-white p-6 shadow-lg text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Pedido Criado!</h2>
            <p className="text-gray-600 mb-6">Seu pedido foi recebido com sucesso.</p>

            {orderResult.pixCode && (
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-700 mb-2">Pague via PIX:</p>
                <div className="rounded-lg bg-gray-100 p-4">
                  <p className="text-2xl font-bold text-emerald-700 mb-3">{formatCurrency(orderTotal)}</p>
                  {orderResult.pixQrCode && (
                    <img
                      src={orderResult.pixQrCode}
                      alt="QR Code PIX"
                      className="mx-auto mb-3 h-48 w-48"
                    />
                  )}
                  <p className="text-xs text-gray-500 break-all font-mono bg-white p-2 rounded">
                    {orderResult.pixCode}
                  </p>
                  <button
                    onClick={() => navigator.clipboard.writeText(orderResult.pixCode || '')}
                    className="mt-2 text-sm text-emerald-600 font-medium hover:underline"
                  >
                    Copiar cÃ³digo PIX
                  </button>
                </div>
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  <span>Aguardando pagamento...</span>
                </div>
              </div>
            )}

            <button
              onClick={() => navigate(`/loja/${slug}/pedido/${orderResult.id}`)}
              className="w-full rounded-xl bg-emerald-600 px-4 py-3 text-white font-medium hover:bg-emerald-700 transition-colors"
            >
              Acompanhar Pedido
            </button>
          </div>
        </div>
      </div>
    );
  }

  // â”€â”€â”€ Main Store View â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  return (
    <div className="store-page min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-3 flex items-center gap-3">
          {storeInfo.logo ? (
            <img src={storeInfo.logo} alt={storeInfo.name} className="h-10 w-10 rounded-lg object-cover" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-100">
              <Store className="h-5 w-5 text-emerald-600" />
            </div>
          )}
          <h1 className="text-lg font-bold text-gray-800 truncate">{storeInfo.name}</h1>
        </div>
      </header>

      {/* Search */}
      <div className="mx-auto max-w-4xl px-4 pt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar produtos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="mx-auto max-w-4xl px-4 pt-3">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                selectedCategory === cat
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Product Grid */}
      <div className="mx-auto max-w-4xl px-4 pt-4">
        {filteredProducts.length === 0 ? (
          <div className="py-12 text-center text-gray-500">
            <p>Nenhum produto encontrado</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                cartItem={cart.find((i) => i.product.id === product.id)}
                onAdd={() => addToCart(product)}
                onUpdateQty={(qty) => updateCartQuantity(product.id, qty)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Cart FAB */}
      {cart.length > 0 && (
        <div className="fixed bottom-4 left-4 right-4 z-40 mx-auto max-w-md">
          <button
            onClick={() => setShowCart(true)}
            className="flex w-full items-center justify-between rounded-2xl bg-emerald-600 px-5 py-4 text-white shadow-lg hover:bg-emerald-700 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <ShoppingCart className="h-5 w-5" />
                <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold text-emerald-600">
                  {cartItemCount}
                </span>
              </div>
              <span className="font-medium">Ver carrinho</span>
            </div>
            <span className="font-bold">{formatCurrency(cartTotal)}</span>
          </button>
        </div>
      )}

      {/* Cart Drawer */}
      {showCart && (
        <CartDrawer
          cart={cart}
          storeInfo={storeInfo}
          deliveryType={deliveryType}
          onClose={() => setShowCart(false)}
          onRemove={removeFromCart}
          onUpdateQty={updateCartQuantity}
          onCheckout={() => {
            setShowCart(false);
            setShowCheckout(true);
          }}
          cartTotal={cartTotal}
          deliveryFee={deliveryFee}
        />
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          storeInfo={storeInfo}
          cartTotal={cartTotal}
          deliveryFee={deliveryFee}
          orderTotal={orderTotal}
          deliveryType={deliveryType}
          setDeliveryType={setDeliveryType}
          customerName={customerName}
          setCustomerName={setCustomerName}
          customerPhone={customerPhone}
          setCustomerPhone={setCustomerPhone}
          customerAddress={customerAddress}
          setCustomerAddress={setCustomerAddress}
          submitting={submitting}
          onClose={() => setShowCheckout(false)}
          onSubmit={handleSubmitOrder}
        />
      )}
    </div>
  );
}


// â”€â”€â”€ Product Card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function ProductCard({
  product,
  cartItem,
  onAdd,
  onUpdateQty,
}: {
  product: Product;
  cartItem?: CartItem;
  onAdd: () => void;
  onUpdateQty: (qty: number) => void;
}) {
  const isKg = product.unit === 'kg';
  const priceLabel = isKg ? `${formatCurrency(product.price)}/kg` : formatCurrency(product.price);

  return (
    <div className="flex flex-col rounded-xl bg-white border border-gray-100 shadow-sm overflow-hidden">
      {/* Image placeholder */}
      {product.image ? (
        <img src={product.image} alt={product.name} className="h-28 w-full object-cover" />
      ) : (
        <div className="flex h-28 items-center justify-center bg-gray-100">
          <Store className="h-8 w-8 text-gray-300" />
        </div>
      )}

      <div className="flex flex-1 flex-col p-3">
        <h3 className="text-sm font-medium text-gray-800 line-clamp-2 mb-1">{product.name}</h3>
        <p className="text-sm font-bold text-emerald-700 mt-auto">{priceLabel}</p>

        {/* Add / Quantity controls */}
        <div className="mt-2">
          {!cartItem ? (
            <button
              onClick={onAdd}
              disabled={!product.inStock}
              className="w-full rounded-lg bg-emerald-50 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {product.inStock ? 'Adicionar' : 'IndisponÃ­vel'}
            </button>
          ) : (
            <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-2 py-1">
              <button
                onClick={() => onUpdateQty(cartItem.quantity - (isKg ? 0.1 : 1))}
                className="flex h-6 w-6 items-center justify-center rounded-full text-emerald-700 hover:bg-emerald-200"
              >
                <Minus className="h-3 w-3" />
              </button>
              <span className="text-sm font-medium text-emerald-800">
                {isKg ? `${cartItem.quantity.toFixed(1)}kg` : cartItem.quantity}
              </span>
              <button
                onClick={() => onUpdateQty(cartItem.quantity + (isKg ? 0.1 : 1))}
                className="flex h-6 w-6 items-center justify-center rounded-full text-emerald-700 hover:bg-emerald-200"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// â”€â”€â”€ Cart Drawer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function CartDrawer({
  cart,
  storeInfo,
  deliveryType,
  onClose,
  onRemove,
  onUpdateQty,
  onCheckout,
  cartTotal,
  deliveryFee,
}: {
  cart: CartItem[];
  storeInfo: StoreInfo;
  deliveryType: string;
  onClose: () => void;
  onRemove: (id: string) => void;
  onUpdateQty: (id: string, qty: number) => void;
  onCheckout: () => void;
  cartTotal: number;
  deliveryFee: number;
}) {
  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Drawer */}
      <div className="relative mt-auto flex max-h-[85vh] flex-col rounded-t-2xl bg-white">
        {/* Handle */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-lg font-bold text-gray-800">Carrinho</h2>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cart.map((item) => {
            const isKg = item.product.unit === 'kg';
            return (
              <div key={item.product.id} className="flex items-center gap-3 rounded-lg bg-gray-50 p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800">{item.product.name}</p>
                  <p className="text-xs text-gray-500">
                    {formatCurrency(item.product.price)} {isKg ? '/kg' : 'un'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onUpdateQty(item.product.id, item.quantity - (isKg ? 0.1 : 1))}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="w-12 text-center text-sm font-medium text-gray-800">
                    {isKg ? `${item.quantity.toFixed(1)}` : item.quantity}
                  </span>
                  <button
                    onClick={() => onUpdateQty(item.product.id, item.quantity + (isKg ? 0.1 : 1))}
                    className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100"
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-800">
                    {formatCurrency(item.product.price * item.quantity)}
                  </p>
                  <button
                    onClick={() => onRemove(item.product.id)}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Remover
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="border-t border-gray-100 p-4 space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>{formatCurrency(cartTotal)}</span>
          </div>
          {deliveryFee > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Taxa de entrega</span>
              <span>{formatCurrency(deliveryFee)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold text-gray-800 pt-1 border-t border-gray-100">
            <span>Total</span>
            <span>{formatCurrency(cartTotal + deliveryFee)}</span>
          </div>
          <button
            onClick={onCheckout}
            className="mt-3 w-full rounded-xl bg-emerald-600 py-3 text-white font-medium hover:bg-emerald-700 transition-colors"
          >
            Finalizar Pedido
          </button>
        </div>
      </div>
    </div>
  );
}

// â”€â”€â”€ Checkout Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function CheckoutModal({
  storeInfo,
  cartTotal,
  deliveryFee,
  orderTotal,
  deliveryType,
  setDeliveryType,
  customerName,
  setCustomerName,
  customerPhone,
  setCustomerPhone,
  customerAddress,
  setCustomerAddress,
  submitting,
  onClose,
  onSubmit,
}: {
  storeInfo: StoreInfo;
  cartTotal: number;
  deliveryFee: number;
  orderTotal: number;
  deliveryType: 'entrega' | 'retirada';
  setDeliveryType: (t: 'entrega' | 'retirada') => void;
  customerName: string;
  setCustomerName: (v: string) => void;
  customerPhone: string;
  setCustomerPhone: (v: string) => void;
  customerAddress: string;
  setCustomerAddress: (v: string) => void;
  submitting: boolean;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const canSubmit =
    customerName.trim() &&
    customerPhone.trim() &&
    (deliveryType === 'retirada' || customerAddress.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">Finalizar Pedido</h2>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Delivery Type */}
        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">Tipo de entrega</label>
          <div className="grid grid-cols-2 gap-2">
            {storeInfo.acceptsDelivery && (
              <button
                onClick={() => setDeliveryType('entrega')}
                className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                  deliveryType === 'entrega'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <Truck className="h-4 w-4" />
                Entrega
              </button>
            )}
            {storeInfo.acceptsPickup && (
              <button
                onClick={() => setDeliveryType('retirada')}
                className={`flex items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                  deliveryType === 'retirada'
                    ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <Store className="h-4 w-4" />
                Retirada
              </button>
            )}
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-3 mb-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Nome</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Seu nome"
                className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Telefone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="(00) 00000-0000"
                className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              />
            </div>
          </div>

          {deliveryType === 'entrega' && (
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">EndereÃ§o</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <textarea
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="Rua, nÃºmero, bairro..."
                  rows={2}
                  className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-4 text-sm text-gray-800 placeholder:text-gray-400 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100 resize-none"
                />
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="rounded-xl bg-gray-50 p-3 mb-4 space-y-1">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal</span>
            <span>{formatCurrency(cartTotal)}</span>
          </div>
          {deliveryFee > 0 && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>Taxa de entrega</span>
              <span>{formatCurrency(deliveryFee)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold text-gray-800 pt-1 border-t border-gray-100">
            <span>Total</span>
            <span>{formatCurrency(orderTotal)}</span>
          </div>
        </div>

        {/* Submit */}
        <button
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          className="w-full rounded-xl bg-emerald-600 py-3 text-white font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Enviando...
            </>
          ) : (
            `Confirmar Pedido â€¢ ${formatCurrency(orderTotal)}`
          )}
        </button>
      </div>
    </div>
  );
}
